/**
 * YMS API Types - Matching Xamarin DTOs exactly
 */

// ─────────────────────────────────────────────────────────────────
// Configuration API Types
// ─────────────────────────────────────────────────────────────────

export interface ConfigRequestDTO {
  device_make: string;
  device_model: string;
  device_os: string;
  device_os_version: string;
  device_build_version: string;
  current_app_version: string;
  app_center_id: string;
  device_latitude: number;
  device_longitude: number;
}

export interface CountryCode {
  code: string;
  name: string;
  short_code: string;
}

export interface PlantCoordinates {
  lat: number;
  lng: number;
}

export interface PlantLocation {
  type: string;
  coordinates: number[][][];
}

export interface PlantInfo {
  _id: string;
  org_id: number;
  plant_code: string;
  plant_name: string;
  task_acceptance_criteria: number;
  address: string;
  org_tz: string;
  org_city: string;
  org_state_prov: string;
  org_postal_code: string;
  org_gy_ro_url: string;
  move_task_acceptance_criteria: number;
  audit_task_acceptance_criteria: number;
  is_enabled: number;
  location?: PlantLocation;
  plant_coordinates?: PlantCoordinates;
}

export interface CarrerInfo {
  _id: string;
  carrier_name: string;
}

export interface CarrierInfo {
  _id: string;
  carrier_name: string;
  carrier_code: string;
  is_enabled: number;
}

export interface DeviceResponse {
  device_platform: string;
  is_enabled: number;
  _id: string;
  device_make: string;
  device_model: string;
  device_os: string;
  device_os_version: string;
  device_build_version: string;
  current_app_version: string;
  app_center_id: string;
  device_latitude: string;
  device_longitude: string;
  created_on: string;
  updated_on: string;
  createdAt: string;
  updatedAt: string;
  user: string;
}

export interface AppSettings {
  ACL_DOCK_DOOR_STATUS_CHANGE: boolean;
  ANDROID_APP_BUILD_VERSION: string;
  ANDROID_APP_UPDATE_URL: string;
  APP_AUTO_LOGOUT_TIME: string;
  APP_AUTO_LOGOUT_TIMEOUT: string;
  APP_GUIDE_URL: string;
  APP_GUIDE_URL_MOBILE: string;
  APP_GUIDE_URL_SPANISH: string;
  APP_INACTIVITY_TIMEOUT: string;
  APP_INACTIVITY_START_TIME: string;
  APP_MA_AUTH_TYPE: string;
  APP_MAINTENANCE: boolean;
  APP_SESSION_TIMEOUT: string;
  APP_UPDATE: string;
  APP_UPDATER: boolean;
  AUDIT_TASK_ACCEPTANCE_CRITERIA: number;
  AUDIT_TASK_COMPLETION_TIME_LIMIT: number;
  AUTO_REFRESH_SCREENS: string;
  AUTO_REFRESH_TIMEOUT: number;
  COMPLETE_ACTION_EXCEPTION: string;
  COMPLETE_TASK_FLOW: string;
  COMPLETE_TASK_FLOW_SPANISH: string;
  COUNTRY_CODES: string;
  ENV: string;
  FUNCTION_CODES: string;
  GEOFENCE: boolean;
  GRAPHICALYARD_WEBVIEW: boolean;
  GRAPHICAL_YARD_MOBILE_ISENABLED: boolean;
  GRAPHICAL_YARD_URL: string;
  IOS_APP_BUILD_VERSION: string;
  IOS_APP_UPDATE_URL: string;
  LIST_LIMIT: string;
  LOCATIONS_DEBUGGER: boolean;
  MA_ACCESS_REQUEST: string;
  MA_API_BASE_URL: string;
  MIDDLEWARE_API_BASE_URL: string;
  MIDDLEWARE_FUNCTION_CODES: string;
  MOVE_RESTRICTION_LIMIT: string;
  MOVE_TASK_ACCEPTANCE_CRITERIA: number;
  RESEND_OTP_TIMEOUT: string;
  SHOW_GRAPHICAL_YARD: boolean;
  SHOW_SIGNALR_NOTIFICATION_DIALOG: boolean;
  TASK_ACCEPTANCE_CRITERIA: string;
  TASK_COMPLETION_TIME_LIMIT: string;
  TASK_LIST_TIME_LIMIT: string;
  TRAILER_MASTER_DATA: string;
  TRAILER_MASTER_DATA_SPANISH: string;
  WINDOWS_APP_BUILD_VERSION: string;
  WINDOWS_APP_UPDATE_URL: string;
  YMS_AUDIT_SUMMARY_URL: string;
  YMS_DASHBOARD_URL: string;
  YMS_DASHBOARD_WEBVIEW: boolean;
  [key: string]: string | number | boolean; // Allow additional settings (GRAPHICAL_YARD_URL, SHOW_GRAPHICAL_YARD, etc.)
}

// Function codes for API endpoints - parsed from settings.FUNCTION_CODES
export interface FunctionCodes {
  config?: string;
  user_send_otp?: string;
  user_verify_otp?: string;
  users?: string;
  plants?: string;
  settings?: string;
  carriers?: string;
  suggestions?: string;
  devices?: string;
  user_logs?: string;
  user_activities?: string;
  refresh_token?: string;
  carrier_list?: string;
  user_sign_up?: string;
  notifications?: string;
  negotiate?: string;
  geofence?: string;
  plants_trigger?: string;
  user_details?: string;
  tasks?: string;
  api_traces?: string;
  notifications_history?: string;
  [key: string]: string | undefined;
}

export interface ConfigurationModel {
  settings: AppSettings;
  device: DeviceResponse;
}

// App Guide slide — parsed from APP_GUIDE_URL_MOBILE config JSON
// Matches Xamarin AppGuideImageModel (Image, Header, Text — lowercase in JSON)
export interface AppGuideSlide {
  image: string;
  header: string;
  text: string;
}

export interface ConfigResponseDTO {
  data: ConfigurationModel;
  status: number;
  statusText: string;
}

// ─────────────────────────────────────────────────────────────────
// Authentication API Types
// ─────────────────────────────────────────────────────────────────

export interface LoginRequestDTO {
  mobile_no: string;
  country_code: string;
}

export interface VerifyOTPRequestDTO {
  mobile_no: string;
  country_code: string;
  otp: string;
  lat?: number;
  lng?: number;
  device_id?: string;
}

// ─────────────────────────────────────────────────────────────────
// Verify OTP Response Types - Matching Xamarin exactly
// ─────────────────────────────────────────────────────────────────

export interface UserProfile {
  id: string;
  mobile_no: string;
  country_code: string;
  first_name: string;
  last_name: string;
  email?: string;
  profile_image?: string;
  role: string;
  plant_id?: number;
  plant_name?: string;
  is_active: boolean;
}

export interface AuthResponseDTO {
  status: boolean;
  message: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: UserProfile;
}

// ─────────────────────────────────────────────────────────────────
// Device Info Helper Types
// ─────────────────────────────────────────────────────────────────

export interface DeviceInfo {
  deviceMake: string;
  deviceModel: string;
  deviceOS: string;
  deviceOSVersion: string;
  deviceBuildVersion: string;
  currentAppVersion: string;
  appCenterId: string;
  deviceLatitude: number;
  deviceLongitude: number;
}

// ─────────────────────────────────────────────────────────────────
// API Error Types
// ─────────────────────────────────────────────────────────────────

export interface ApiErrorResponse {
  status: boolean;
  message: string;
  error_code?: string;
  errors?: Record<string, string[]>;
}

// ─────────────────────────────────────────────────────────────────
// Task Management Types - Matching Xamarin DTOs exactly
// ─────────────────────────────────────────────────────────────────

export const TASK_STATUS = {
  UPCOMING: 20,
  ACCEPTED: 30,
  IN_PROGRESS: 40,
  COMPLETED: 60,
} as const;

export type TaskType = 'Move' | 'Audit';

export type TaskTabType = 'upcoming' | 'myTasks' | 'completed';

// ── Transmission Header (shared by all middleware requests) ──

export interface TransmissionHeader {
  TransmissionDateTime: string;
  OriginatingSystem: string;
  TransmissionUser: string;
  RoutingCode: string;
  MessageType: string;
  MessageId: string;
}

// ── Get Task Request ──

export interface GetTaskRequestBody {
  OrgId: string;
  ResponseRecordLimit: number;
  TaskType: string | null;
  FilterString: string;
  SortOrder: string;
  YMSTaskType: string;
}

export interface GetTaskRequestModel {
  Payload: {
    TransmissionHeader: TransmissionHeader;
    Body: GetTaskRequestBody;
  };
  TraceData?: Record<string, unknown>;
}

// ── Task Detail (response item) ──

export interface TaskDetail {
  TaskId: string;
  TaskType: TaskType;
  Status: number;
  Priority: number;
  PriorityName: string;
  TrailerName: string;
  TrailerId: string;
  CarrierCompanyName: string;
  CarrierName: string;
  MoveSourceType: string;
  MoveSourceZone: string;
  MoveSourceName: string;
  MoveDestinationType: string;
  MoveDestinationZone: string;
  MoveDestinationName: string;
  AuditLocationType: string;
  AuditLocationZone: string;
  AuditLocationName: string;
  CreatedDatetime: string;
  LastUpdatedDatetime: string;
  TaskStartDateTime: string;
  TaskEndDateTime: string;
  TaskDescription: string;
  TaskComments: string;
  TrailerComments: string;
  AssignedUserId: string;
  AssignedUserName: string;
  ExceptionCode: string;
  ExceptionText: string;
  ElapsedTime: number;
  AlternateTrailerName: string;
  AlternateCarrier: string;
  AlternateDestinationZone: string;
  AlternateDestinationName: string;
  // UI-only state
  isExpanded?: boolean;
}

// ── Get Task Response ──

export interface GetTaskResponseModel {
  Payload: {
    TransmissionHeader: TransmissionHeader;
    Body: {
      TaskDetails: TaskDetail[];
    };
  };
}

export interface GetTaskResponseDetailItem {
  ActionCode: string;
  OrgId: string;
  TaskId: string;
  TaskType: string;
  Status: string;
  AssignedUserId: string;
  CreatedSource: string;
  CreatedDatetime: string;
  LastUpdatedDatetime: string;
  LastUpdatedSource: string;
  Priority: string;
  PriorityName: string;
  YardId: string;
  TaskStartDateTime: string;
  TaskEndDateTime: string;
  TaskReleaseDatetime: string;
  TaskComments: string;
  TrailerId: string;
  TrailerName: string;
  TrailerComments: string;
  CarrierCompanyName: string;
  CarrierName: string;
  AuditLocationType: string;
  AuditLocationZone: string;
  AuditLocationName: string;
  MoveSourceType: string;
  MoveSourceZone: string;
  MoveSourceName: string;
  MoveDestinationType: string;
  MoveDestinationZone: string;
  MoveDestinationName: string;
  CurrentLoggedInUser: string;
  ElapsedTime: string;
  ExceptionCode: string;
  ExceptionText: string;
  TaskAssignedTime: string;
  AlternateTrailerValid: string;
  AlternateCarrier: string;
  AlternateTrailerName: string;
  AlternateDestinationZone: string;
  AlternateDestinationName: string;
  TaskDescription: string;
}

export interface GetTaskResponseDetailsModel {
  Payload: {
    TransmissionHeader: TransmissionHeader;
    Body: {
      Status: string;
      StatusMessage: string;
      StatusCode: string;
      NumberOfRecords: string;
      Task: {
        TaskDetails: GetTaskResponseDetailItem[];
      };
    };
  };
}

// ── Update Task Request ──

// Matches Xamarin UpdateTaskRequestUpdateTaskDetail
export interface UpdateTaskDetail {
  TaskType: string;
  OrgId: string;
  TaskId: string;
  UserId: string;
  ActionType: string;
  ActionDatetime: string;
  TaskComments: string;
  TrailerId: string;
  TrailerName: string;
  TrailerComments: string;
  CarrierCompanyName: string;
  CarrierName: string;
  // Complete flow extras (second Xamarin overload)
  ExceptionCode?: string | undefined;
  ExceptionText?: string | undefined;
  AlternateTrailerName?: string | undefined;
  AlternateCarrier?: string | undefined;
  AlternateDestinationZone?: string | undefined;
  AlternateDestinationName?: string | undefined;
  AlternateTrailerValid?: string | undefined;
  OverrideReasonCode?: string | undefined;
}

// Matches Xamarin UpdateTaskRequestModel
export interface UpdateTaskRequestModel {
  Payload: {
    TransmissionHeader: TransmissionHeader;
    Body: {
      OrgId: string;
      StopOnFirstActionError: string;
      UpdateTaskDetails: UpdateTaskDetail[];
    };
  };
  TraceData?:
    | {
        User: string;
        Plant: string;
        SessionId: string;
      }
    | undefined;
}

// ── Update Task Response ──

export interface UpdateTaskResponseModel {
  Payload: {
    Body: {
      UpdateTaskDetails: TaskDetail[];
      InProgressTask?: TaskDetail;
    };
  };
}

// ── Feedback / Suggestions ── Matches Xamarin FeedbackRequestDTO / FeedbackResponseModel

export interface FeedbackRequestDTO {
  user: string;
  title: string;
  description: string;
}

export interface FeedbackResponseDTO {
  status: string;
  message: string;
}

// ── Middleware Function Codes (from MIDDLEWARE_FUNCTION_CODES setting) ──

export interface MiddlewareFunctionCodes {
  yard_capacity?: string;
  move_trailers_list?: string;
  move_locations_list?: string;
  create_move_task?: string;
  update_trailer?: string;
  move_task_list?: string;
  trailer_displaced_summary?: string;
  notifications?: string;
  update_task?: string;
  [key: string]: string | undefined;
}

// ── Complete Task Flow (from COMPLETE_TASK_FLOW config) ──

export interface QuestionFlowNode {
  Question: string;
  QuestionType: 'YES/NO' | 'TRAILER' | 'LOCATION' | 'COMPLETE' | 'EXCEPTION' | 'RENAMETRAILER';
  YesOption: QuestionFlowNode | null;
  NoOption: QuestionFlowNode | null;
}

export interface CompleteTaskFlow {
  move: QuestionFlowNode;
  audit: QuestionFlowNode;
  fix: QuestionFlowNode;
  audit_without_trailer: QuestionFlowNode;
  fix_without_trailer: QuestionFlowNode;
}

// ── Trailer and Location for complete flow ──

export interface TrailerInfo {
  TrailerName: string;
  TrailerId: string;
  CarrierName: string;
  CarrierCompanyName: string;
}

export interface LocationInfo {
  LocationName: string;
  LocationZone: string;
  LocationType: string;
}

// ── Complete Action Exceptions ──

export interface ExceptionItem {
  code: string;
  text: string;
}

export interface CompleteActionExceptions {
  move: ExceptionItem[];
  audit: ExceptionItem[];
  audit_without_trailer: ExceptionItem[];
}

// ── Override Reason Code (from TRAILER_MASTER_DATA config) ──

export interface OverrideReasonCodeInfo {
  OverrideReasonCode: string;
  OverrideReasonName: string;
}

// ── Complete Flow Answers (used by complete task mutations) ──

export interface CompleteFlowAnswers {
  exceptionCode?: string;
  exceptionText?: string;
  alternateTrailerName?: string;
  alternateCarrier?: string;
  alternateDestinationZone?: string;
  alternateDestinationName?: string;
  alternateTrailerValid?: string;
  renamedTrailer?: string;
  selectedTrailer?: TrailerInfo;
  selectedLocation?: LocationInfo;
  taskComments?: string;
  trailerComments?: string;
  isOtherTrailer?: boolean;
  otherTrailerText?: string;
  otherCarrierText?: string;
  overrideReasonCode?: string;
  overrideReasonTaskComment?: string;
}

// ── Notification ── Matches Xamarin NotificationResponseDTO exactly

export interface NotificationResponseDTO {
  _id: string;
  action_code: string;
  org_id: number;
  task_id: number | null;
  task_type: string;
  status: string;
  assigned_user_id: string;
  created_source: string;
  created_datetime: string;
  last_updated_datetime: string;
  last_updated_source: string;
  priority: number | null;
  yard_id: string;
  task_start_datetime: string | null;
  task_end_datetime: string | null;
  task_release_datetime: string;
  task_comments: string;
  trailer_id: number | null;
  trailer_name: string;
  trailer_comments: string;
  carrier_company_name: string;
  carrier_name: string;
  audit_locn_type: string;
  audit_locn_zone: string;
  audit_locn_name: string;
  move_src_type: string;
  move_src_zone: string;
  move_src_name: string;
  move_dst_type: string;
  move_dst_zone: string;
  move_dst_name: string;
  createdAt: string;
  updatedAt: string;
  notification_type: string;
  carrier_scac: string;
  trailer_status: string;
  curr_locn_name: string;
}

// Matches Xamarin NotificationModel (UI model with computed fields)
export interface NotificationModel {
  id: string;
  updatedAt: string;
  actionCode: string;
  orgId: number;
  trailerId: number | null;
  trailerName: string;
  trailerComments: string;
  carrierName: string;
  carrierScac: string;
  trailerStatus: string;
  currentLocation: string;
  createdAt: string;
  taskId: string;
  taskType: string;
  status: string;
  assignedUserId: string;
  createdSource: string;
  createdDateTime: string;
  lastUpdatedDateTime: string;
  lastUpdatedSource: string;
  priority: number;
  yardId: string;
  taskStartDateTime: string;
  taskEndDateTime: string;
  taskReleaseDatetime: string;
  taskComments: string;
  carrierCompanyName: string;
  auditLocationType: string;
  auditLocationZone: string;
  auditLocationName: string;
  moveSourceType: string;
  moveSourceZone: string;
  moveSourceName: string;
  moveDestinationType: string;
  moveDestinationZone: string;
  moveDestinationName: string;
  notificationType: string;
  updatedAgo: string;
  isNew: boolean;
  taskDescription: string;
  trailerTypeStatusName: string;
  isShowTaskNotification: boolean;
  isShowTrailerNotification: boolean;
}

// ── SignalR Types ── Matches Xamarin SignalRNofiticationService DTOs

export interface SignalRNotificationDataDTO {
  task_type: string;
  trailer_name: string;
  move_src_name: string;
  move_dst_name: string;
  audit_locn_name: string;
  task_id: string;
  [key: string]: string | number | null | undefined;
}

export interface SignalRNotificationPayloadDTO {
  notifications_type: string;
  alert_type: string;
  data: SignalRNotificationDataDTO;
}

export interface SignalRNegotiateResponseDTO {
  status: boolean;
  url: string;
  accessToken: string;
}
